import React, { useEffect, useState } from 'react';
import { loader } from 'graphql.macro';
import { useQuery, useLazyQuery, useSubscription } from '@apollo/react-hooks';
import { RouteComponentProps, useParams } from '@reach/router';
import { DocumentsInfoPage } from './DocumentsInfoPage';
import { GET_DOCUMENTS_FROM_PC_getDocumentsFromPC_documentsList as account_documents_item } from './graphql/__generated__/GET_DOCUMENTS_FROM_PC';
import { Box } from 'grommet';
import b64toBlobURL from '../../util/createBase64BlobURL';

const on_process_result = loader(
  '../../components/gpaCancelPolicy/graphql/Run_Subscription_Result.graphql'
);
const get_account_documents = loader('./graphql/Get_Documents_From_PC.graphql');
const get_policies_by_account = loader(
  './graphql/Get_Policies_By_Account.graphql'
);
const get_document_by_docid = loader('./graphql/Get_Document_By_Id.graphql');

export interface GET_POLICIES_BY_ACCOUNT_account_items_policies_items {
  __typename: 'Policy';
  policy_type: string | null;
  policy_number: string | null;
  expiration_date: any | null;
}

export default (_: RouteComponentProps) => {
  const { accountNumber } = useParams();
  const [items, setItems] = useState<account_documents_item[]>([]);
  const [isProcessing, setIsProcessing] = useState(true);
  const [processingError, setProcessingError] = useState<string | null>(null);
  const [hasReceivedData, setHasReceivedData] = useState(false);

  // Subscribe to document processing results
  const { data: subscriptionData, error: subscriptionError } = useSubscription(
    on_process_result,
    {
      variables: {
        policy_number: accountNumber
      },
      shouldResubscribe: true
    }
  );

  // Trigger the backend to fetch documents (query will timeout but that's OK)
  const { data: queryData, loading: queryLoading } = useQuery(get_account_documents, {
    variables: {
      account_number: accountNumber
    },
    fetchPolicy: 'network-only',
    // Ignore query errors - we rely on subscription for data
    onError: (err) => {
      console.log('Query timeout (expected for large accounts) - waiting for subscription data...');
    }
  });

  // Handle query data (for quick responses < 30 seconds)
  useEffect(() => {
    if (queryData?.getDocumentsFromPC?.documentsList) {
      setItems(queryData.getDocumentsFromPC.documentsList);
      setIsProcessing(false);
      setHasReceivedData(true);
    }
  }, [queryData]);

  // Handle subscription data (for responses > 30 seconds)
  useEffect(() => {
    if (subscriptionData?.onProcessResult?.message) {
      try {
        const json_data = JSON.parse(subscriptionData.onProcessResult.message);
        if (json_data?.documentsList) {
          setItems(json_data.documentsList);
          setIsProcessing(false);
          setHasReceivedData(true);
          setProcessingError(null); // Clear any previous errors
        }
      } catch (error) {
        console.error('Error parsing subscription data:', error);
        setProcessingError('Error processing document data');
        setIsProcessing(false);
      }
    }
  }, [subscriptionData]);

  // Handle subscription errors
  useEffect(() => {
    if (subscriptionError) {
      console.error('Subscription error:', subscriptionError);
      setProcessingError(subscriptionError.message);
      setIsProcessing(false);
    }
  }, [subscriptionError]);

  // Timeout fallback: if no data received after 2 minutes, show error
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (!hasReceivedData && isProcessing) {
        setProcessingError('Document fetch is taking longer than expected. Please try again later.');
        setIsProcessing(false);
      }
    }, 150000); // 2.5 minutes

    return () => clearTimeout(timeout);
  }, [hasReceivedData, isProcessing]);
  
  let policies:
    | GET_POLICIES_BY_ACCOUNT_account_items_policies_items[]
    | [] = [];
  const { data: policyList } = useQuery(get_policies_by_account, {
    variables: {
      account_number: accountNumber
    },
    fetchPolicy: 'network-only'
  });
  if (policyList) {
    policies = policyList?.getPoliciesByAccount?.PoliciesList || [];
  }

  const download = (contents: string) => {
    setTimeout(() => {
      const newWindow = window.open('about:blank');
      const newTab =
        newWindow &&
        newWindow.document.body.appendChild(
          newWindow.document.createElement('iframe')
        );
      if (newTab) {
        newTab.width = '100%';
        newTab.height = '100%';
        newTab.setAttribute('style', 'border: 0');
        newTab.src = contents;
      }
    }, 0);
  };

  const openHtmlContent = (content: string) => {
    try {
      let htmlString = content;

      if (
        /^([A-Za-z0-9+/=]+\s*)+$/.test(content) ||
        content.startsWith('data:')
      ) {
        const cleanBase64 = content.replace(/^data:.*;base64,/, '');
        htmlString = atob(cleanBase64);
      }

      const blob = new Blob([htmlString], { type: 'text/html' });
      const url = URL.createObjectURL(blob);

      window.open(url, '_blank');
    } catch (err) {
      console.error('Error decoding HTML:', err);
    }
  };

  const [downloadDocument] = useLazyQuery(get_document_by_docid, {
    fetchPolicy: 'network-only',
    onCompleted: data => {
      const documentData = data?.getQuotesInformation;
      let source;
      let fileType = 'htm';
      fileType = documentData.filetype;
      switch (fileType) {
        case 'pdf':
          source = b64toBlobURL(documentData.contents, 'application/pdf');
          download(source);
          break;
        case 'htm':
          openHtmlContent(documentData.contents);
          break;
        default:
          throw new Error(`Invalid file type: ${fileType}`);
      }
    },
    onError: error => {
      console.log('Error Reason: ', error.message);
    }
  });

  return (
    <Box>
      <DocumentsInfoPage
        isLoading={isProcessing}
        error={processingError ? new Error(processingError) : undefined}
        accountNumber={accountNumber}
        information={items}
        policies={policies}
        downloadDocument={downloadDocument}
      />
    </Box>
  );
};
